import { httpApi } from '@app/api/http.api';

/** Where a setting's current value comes from. `unreadable` means a row
 * exists in the DB but will not decrypt with the master key this environment
 * has — the UI must ask for the key again rather than imply all is well. */
export type SettingSource = 'db' | 'env' | 'unreadable';

interface SettingMeta {
  source: SettingSource;
  /** ISO timestamp, or null when the value comes from `.env`. */
  updatedAt: string | null;
  /** Admin user id, or null when the value comes from `.env`. */
  updatedBy: number | null;
}

export interface SecretSetting extends SettingMeta {
  /** `sk-proj-…6ecd`. Null when nothing is configured, or when the stored
   * value is unreadable. The full key is NEVER sent to the browser. */
  masked: string | null;
  configured: boolean;
}

export interface ValueSetting extends SettingMeta {
  value: string | null;
}

export interface AiSettings {
  openaiApiKey: SecretSetting;
  plannerModel: ValueSetting;
  imageModel: ValueSetting;
  imageQuality: ValueSetting;
  /** Server-published whitelist — the selects render from this rather than
   * hard-coding model names the backend might narrow later. */
  allowed: {
    plannerModel: string[];
    imageModel: string[];
    imageQuality: string[];
  };
}

/**
 * Only the keys PRESENT here are touched; `null` clears that setting so
 * `.env` applies again. Sending `{ imageQuality: 'medium' }` must leave the
 * API key alone — hence the explicit-null convention rather than sending the
 * whole object every time.
 */
export interface AiSettingsUpdate {
  openaiApiKey?: string | null;
  plannerModel?: string | null;
  imageModel?: string | null;
  imageQuality?: string | null;
}

export interface KeyProbeResult {
  ok: boolean;
  status: number | null;
  message?: string;
}

export const getAiSettings = (): Promise<AiSettings> =>
  httpApi.get<AiSettings>('ai-draw/admin/settings').then(({ data }) => data);

export const updateAiSettings = (body: AiSettingsUpdate): Promise<AiSettings> =>
  httpApi.put<AiSettings>('ai-draw/admin/settings', body).then(({ data }) => data);

/** Probes a candidate key (or the stored one when `apiKey` is omitted).
 * A key OpenAI rejects comes back `{ ok: false }` with a 200 — it is an
 * answer, not a failure — so this only rejects when OpenAI is unreachable. */
export const testAiKey = (apiKey?: string): Promise<KeyProbeResult> =>
  httpApi.post<KeyProbeResult>('ai-draw/admin/settings/test-key', apiKey ? { apiKey } : {}).then(({ data }) => data);
